import { useEffect, useRef, useState } from "react";

const SIZE = 96;
const EYE_OFFSET_X = 16;
const EYE_OFFSET_Y = -2;
const EYE_RADIUS = 7;
const PUPIL_RADIUS = 3.5;
const MAX_PUPIL_TRAVEL = EYE_RADIUS - PUPIL_RADIUS - 0.5;

export default function PeekMascot({ covered = false }) {
  const wrapRef = useRef(null);
  const [pupil, setPupil] = useState({ dx: 0, dy: 0 });

  useEffect(() => {
    if (covered) return;
    const onMove = (e) => {
      const el = wrapRef.current;
      if (!el) return;
      const box = el.getBoundingClientRect();
      const cx = box.left + box.width / 2;
      const cy = box.top + box.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.hypot(dx, dy) || 1;
      const max = MAX_PUPIL_TRAVEL;
      const k = Math.min(1, dist / 140);
      setPupil({ dx: (dx / dist) * max * k, dy: (dy / dist) * max * k });
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [covered]);

  const cx = SIZE / 2;
  const cy = SIZE / 2 + 4;

  return (
    <div
      ref={wrapRef}
      aria-hidden="true"
      style={{
        width: SIZE,
        height: SIZE,
        margin: "0 auto 14px",
        userSelect: "none",
        pointerEvents: "none",
      }}
    >
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} width={SIZE} height={SIZE}>
        <defs>
          <linearGradient id="botBody" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#8B5CF6" />
            <stop offset="100%" stopColor="#7C3AED" />
          </linearGradient>
          <linearGradient id="botHand" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#A78BFA" />
            <stop offset="100%" stopColor="#7C3AED" />
          </linearGradient>
        </defs>

        {/* Antenna */}
        <line
          x1={cx}
          y1={cy - 38}
          x2={cx}
          y2={cy - 30}
          stroke="#A78BFA"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <circle cx={cx} cy={cy - 40} r="3" fill="#F59E0B">
          <animate
            attributeName="r"
            values="3;3.6;3"
            dur="1.6s"
            repeatCount="indefinite"
          />
        </circle>

        {/* Head */}
        <rect
          x={cx - 30}
          y={cy - 30}
          width="60"
          height="50"
          rx="14"
          fill="url(#botBody)"
        />

        {/* Cheek highlight */}
        <ellipse
          cx={cx - 14}
          cy={cy + 10}
          rx="5"
          ry="2.5"
          fill="#F472B6"
          opacity="0.55"
        />
        <ellipse
          cx={cx + 14}
          cy={cy + 10}
          rx="5"
          ry="2.5"
          fill="#F472B6"
          opacity="0.55"
        />

        {/* Eyes — whites */}
        <circle
          cx={cx - EYE_OFFSET_X}
          cy={cy + EYE_OFFSET_Y}
          r={EYE_RADIUS}
          fill="#FFFFFF"
        />
        <circle
          cx={cx + EYE_OFFSET_X}
          cy={cy + EYE_OFFSET_Y}
          r={EYE_RADIUS}
          fill="#FFFFFF"
        />

        {/* Pupils — hidden when covered */}
        {!covered && (
          <>
            <circle
              cx={cx - EYE_OFFSET_X + pupil.dx}
              cy={cy + EYE_OFFSET_Y + pupil.dy}
              r={PUPIL_RADIUS}
              fill="#1E1B4B"
              style={{ transition: "cx 0.06s linear, cy 0.06s linear" }}
            />
            <circle
              cx={cx + EYE_OFFSET_X + pupil.dx}
              cy={cy + EYE_OFFSET_Y + pupil.dy}
              r={PUPIL_RADIUS}
              fill="#1E1B4B"
              style={{ transition: "cx 0.06s linear, cy 0.06s linear" }}
            />
            {/* Tiny glints */}
            <circle
              cx={cx - EYE_OFFSET_X + pupil.dx + 1.2}
              cy={cy + EYE_OFFSET_Y + pupil.dy - 1.2}
              r="0.9"
              fill="#FFFFFF"
            />
            <circle
              cx={cx + EYE_OFFSET_X + pupil.dx + 1.2}
              cy={cy + EYE_OFFSET_Y + pupil.dy - 1.2}
              r="0.9"
              fill="#FFFFFF"
            />
          </>
        )}

        {/* Mouth */}
        {covered ? (
          // small "oh" mouth when hiding
          <ellipse cx={cx} cy={cy + 14} rx="3" ry="2.2" fill="#1E1B4B" />
        ) : (
          // smile
          <path
            d={`M ${cx - 7} ${cy + 12} Q ${cx} ${cy + 18} ${cx + 7} ${cy + 12}`}
            stroke="#1E1B4B"
            strokeWidth="2"
            strokeLinecap="round"
            fill="none"
          />
        )}

        {/* Hands covering eyes when password focused */}
        {covered && (
          <g>
            <g
              style={{
                transformOrigin: `${cx - EYE_OFFSET_X}px ${cy + EYE_OFFSET_Y}px`,
              }}
            >
              <ellipse
                cx={cx - EYE_OFFSET_X}
                cy={cy + EYE_OFFSET_Y}
                rx="11"
                ry="9"
                fill="url(#botHand)"
                stroke="#5B21B6"
                strokeWidth="1"
              />
              {/* tiny fingers gap */}
              <line
                x1={cx - EYE_OFFSET_X - 4}
                y1={cy + EYE_OFFSET_Y - 7}
                x2={cx - EYE_OFFSET_X - 4}
                y2={cy + EYE_OFFSET_Y + 7}
                stroke="#5B21B6"
                strokeWidth="0.7"
                opacity="0.6"
              />
              <line
                x1={cx - EYE_OFFSET_X + 0}
                y1={cy + EYE_OFFSET_Y - 8}
                x2={cx - EYE_OFFSET_X + 0}
                y2={cy + EYE_OFFSET_Y + 8}
                stroke="#5B21B6"
                strokeWidth="0.7"
                opacity="0.6"
              />
              <line
                x1={cx - EYE_OFFSET_X + 4}
                y1={cy + EYE_OFFSET_Y - 7}
                x2={cx - EYE_OFFSET_X + 4}
                y2={cy + EYE_OFFSET_Y + 7}
                stroke="#5B21B6"
                strokeWidth="0.7"
                opacity="0.6"
              />
            </g>
            <g
              style={{
                transformOrigin: `${cx + EYE_OFFSET_X}px ${cy + EYE_OFFSET_Y}px`,
              }}
            >
              <ellipse
                cx={cx + EYE_OFFSET_X}
                cy={cy + EYE_OFFSET_Y}
                rx="11"
                ry="9"
                fill="url(#botHand)"
                stroke="#5B21B6"
                strokeWidth="1"
              />
              <line
                x1={cx + EYE_OFFSET_X - 4}
                y1={cy + EYE_OFFSET_Y - 7}
                x2={cx + EYE_OFFSET_X - 4}
                y2={cy + EYE_OFFSET_Y + 7}
                stroke="#5B21B6"
                strokeWidth="0.7"
                opacity="0.6"
              />
              <line
                x1={cx + EYE_OFFSET_X + 0}
                y1={cy + EYE_OFFSET_Y - 8}
                x2={cx + EYE_OFFSET_X + 0}
                y2={cy + EYE_OFFSET_Y + 8}
                stroke="#5B21B6"
                strokeWidth="0.7"
                opacity="0.6"
              />
              <line
                x1={cx + EYE_OFFSET_X + 4}
                y1={cy + EYE_OFFSET_Y - 7}
                x2={cx + EYE_OFFSET_X + 4}
                y2={cy + EYE_OFFSET_Y + 7}
                stroke="#5B21B6"
                strokeWidth="0.7"
                opacity="0.6"
              />
            </g>
          </g>
        )}
      </svg>
    </div>
  );
}
